<template>
	<div class="filter-pill">
		<button
			class="filter-pill__operator"
			@click="toggleMenu">
			{{ operatorText }}
		</button>
		<div class="filter-pill__separator" />
		<div class="filter-pill__label">
			<i
				class="filter-pill__icon"
				:class="control.icon" />
			<span>{{ control.name }}</span>
		</div>
		<div class="filter-pill__separator" />
		<button
			class="filter-pill__remove"
			@click="removeFilter()">
			<FontAwesomeIcon icon="fal fa-xmark" />
		</button>
		<Menu
			ref="menu"
			popup
			:model="menuItems" />
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Menu from 'primevue/menu'
import type { Filter } from '@/helpers/filters/Filter'
import type { FilterControls } from '@/helpers/filters/FilterControls'
import type { Filters } from '@/helpers/filters/Filters'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const emit = defineEmits<{
	(e: 'updated'): void
}>()

const props = defineProps<{
	filter: Filter<any>
	controls: FilterControls<any>
	filters: Filters<any>
}>()

const menu = ref<InstanceType<typeof Menu>>()
const operatorText = computed(() => (props.filter.operator === 'is' ? 'is' : 'is not'))
const control = computed(() => {
	return props.controls.items.find((control) => control.key === props.filter.key)!
})

const menuItems = [
	{
		label: 'is',
		icon: 'fal fa-equals',
		command: () => {
			props.filter.operator = 'is'
			emit('updated')
		},
	},
	{
		label: 'is not',
		icon: 'fal fa-not-equal',
		command: () => {
			props.filter.operator = 'is_not'
			emit('updated')
		},
	},
]

function toggleMenu(event: MouseEvent) {
	menu.value?.toggle(event)
}

function removeFilter() {
	props.filters.list.delete(props.filter)
	emit('updated')
}
</script>

<style scoped lang="scss">
.filter-pill {
	display: inline-flex;
	align-items: center;
	border: 1px solid var(--p-surface-200);
	border-radius: 8px;
	background: var(--p-surface-100);
	font-size: 12px;
	color: var(--p-text-color);
	overflow: hidden;

	@media all {
		background: var(--p-surface-900);
		border-color: var(--p-surface-700);
	}

	.filter-pill__operator,
	.filter-pill__remove {
		background: transparent;
		border: none;
		cursor: pointer;
		color: inherit;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 27px;
		min-width: 27px;
		text-align: center;
		white-space: nowrap;

		&:hover {
			background: rgba(100, 100, 100, 0.2);
		}
	}

	.filter-pill__label {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 8px;
	}

	.filter-pill__icon {
		font-size: 12px;
	}
	.filter-pill__separator {
		width: 1px;
		height: 27px;
		background: rgba(100, 100, 100, 0.3);
	}

	@media all {
		.filter-pill__separator {
			background: rgba(100, 100, 100, 0.3);
		}
	}
}
</style>
